import { isDefined, isNonEmpty } from '@sgme/fp';
import type { BackTestResponseTimeSerie } from 'apiConnector/back-test';
import { interval, isWithinInterval, subYears } from 'date-fns';
import type { Action } from 'state/actions';
import type {
  BackTestByTabIdState,
  BackTestFormError,
  BackTestModel,
  DateMode,
  Point,
} from 'state/backTest/backTest.model';
import { initialBackTest, initialBackTestByTabId, nowISODate, tenYearsAgoISODate } from 'state/backTest/backTest.model';
import { addOrReplaceKey, patchKeyIfExist, removeKey, removeKeys, replaceKeyIfExist } from 'state/utils/collection';
export function backTestReducer(
  state: BackTestByTabIdState = initialBackTestByTabId,
  action: Action,
): BackTestByTabIdState {
  switch (action.type) {
    case 'WORKSPACE_TAB_REMOVED':
      return removeKey(state, action.id);
    case 'WORKSPACE_TABS_ALL_REMOVED':
      return removeKeys(state, action.tabIds);
    case 'CREATE_BACK_TEST':
      return addOrReplaceKey(state, action.tabId, initialBackTest(action.quoteId));
    case 'SET_INITIAL_BACK_TEST_QUOTE_LABEL':
      return patchKeyIfExist(state, action.tabId, s => {
        return {
          ...s,
          quoteLabels: {
            ...s.quoteLabels,
            initialQuoteLabel: isDefined(action.quoteLabel) ? action.quoteLabel : undefined,
          },
        };
      });
    case 'SET_SECOND_BACK_TEST_QUOTE_LABEL':
      return patchKeyIfExist(state, action.tabId, s => {
        return {
          ...s,
          quoteLabels: {
            ...s.quoteLabels,
            secondQuoteLabel: isDefined(action.quoteLabel) ? action.quoteLabel : undefined,
          },
        };
      });
    case 'SET_SECOND_BACK_TEST_QUOTE_ID':
      return replaceKeyIfExist(state, action.tabId, s => {
        const quoteIds = { ...s.quoteIds, secondQuoteId: action.quoteId };
        const validationErrors = getFormErrors(s.maturities, s.maturityMode, action.quoteId);
        return {
          ...s,
          quoteIds,
          canAddExtraQuote: false,
          validationErrors,
          canRunBackTest: validationErrors.length === 0,
        };
      });
    case 'REMOVE_SECOND_BACK_TEST_QUOTE_ID':
      return replaceKeyIfExist(state, action.tabId, s => {
        const quoteIds = { ...s.quoteIds, secondQuoteId: undefined };
        const validationErrors = getFormErrors(s.maturities, s.maturityMode);
        return {
          ...s,
          quoteIds,
          canAddExtraQuote: true,
          validationErrors,
          canRunBackTest: validationErrors.length === 0,
        };
      });
    case 'RUN_BACK_TEST':
      return replaceKeyIfExist(state, action.tabId, s => ({
        ...s,
        status: 'RUN_BACK_TEST_LOADING',
        errors: undefined,
        requestedQuoteIds: { ...s.quoteIds },
        requestedQuoteLabels: { ...s.quoteLabels },
      }));
    case 'RUN_BACK_TEST_SUCCEED':
      return replaceKeyIfExist(state, action.tabId, s => ({
        ...s,
        status: 'RUN_BACK_TEST_SUCCESS',
        backTestId: action.backTestId,
      }));
    case 'RUN_BACK_TEST_FAILED':
      return replaceKeyIfExist(state, action.tabId, s => ({
        ...s,
        status: 'RUN_BACK_TEST_ERROR',
        errorMessage: action.errorMessage,
        timeout: action.timeout,
        errorType: action.errorType,
      }));
    case 'GET_BACK_TEST':
      return replaceKeyIfExist(state, action.tabId, s => ({
        ...s,
        status: 'GET_BACK_TEST_LOADING',
        errors: undefined,
      }));
    case 'CANCEL_BACK_TEST':
      return replaceKeyIfExist(state, action.tabId, s => ({
        ...s,
        status: 'CANCEL_BACK_TEST',
        canRunBackTest: false,
      }));
    case 'CANCEL_BACK_TEST_SUCCESS':
    case 'CANCEL_BACK_TEST_FAILURE':
      return replaceKeyIfExist(state, action.tabId, s => ({
        ...s,
        status: 'RUN_BACK_TEST_IDLE',
        backTestId: undefined,
        canRunBackTest: true,
      }));
    case 'GET_BACK_TEST_SUCCEEDED':
      return replaceKeyIfExist(state, action.tabId, s => {
        const { statistics, results } = action.response;
        const irrEvolutionSeries = results.map(r => mapPointsToSerie<number>(r.id, r.internalRateReturns));
        const cumulatedAutocallProbSeries = results.map(r =>
          mapPointsToSerie<string>(r.id, r.cumulatedAutocallProbabilities),
        );
        const errorsResults = action.response.results.filter(result => !!result.status && result.status !== 'OK');
        const newState: BackTestModel = {
          ...s,
          irrEvolutionSeries,
          cumulatedAutocallProbSeries,
          statistics,
          status: 'GET_BACK_TEST_SUCCESS',
        };
        if (errorsResults.length > 0) {
          return {
            ...newState,
            errors: errorsResults.reduce(
              (acc, res) => ({ ...acc, [res.id]: { message: res.message, status: res.status } }),
              {},
            ),
          };
        }
        return { ...newState, errors: undefined };
      });
    case 'GET_BACK_TEST_FAILED':
      return replaceKeyIfExist(state, action.tabId, s => ({
        ...s,
        status: 'GET_BACK_TEST_ERROR',
        errorMessage: action.errorMessage,
        timeout: action.timeout,
        errorType: action.errorType,
      }));

    case 'SET_BACK_TEST_MATURITIES':
      return replaceKeyIfExist(state, action.tabId, s => {
        const validationErrors = getFormErrors(action.maturities, s.maturityMode, s.quoteIds.secondQuoteId);
        return {
          ...s,
          maturities: action.maturities,
          validationErrors,
          canRunBackTest: validationErrors.length === 0,
        };
      });
    case 'SET_BACK_TEST_MATURITIES_MODE':
      return replaceKeyIfExist(state, action.tabId, s => {
        const maturities = action.mode === 'range' ? [tenYearsAgoISODate, nowISODate] : [];
        const validationErrors = getFormErrors(maturities, action.mode);
        return {
          ...s,
          maturities,
          maturityMode: action.mode,
          validationErrors,
          canRunBackTest: validationErrors.length === 0,
        };
      });
    case 'RESET_BACK_TEST':
      return replaceKeyIfExist(state, action.tabId, s => ({
        ...s,
        status: 'RUN_BACK_TEST_IDLE',
        statistics: {},
        cumulatedAutocallProbSeries: [],
        irrEvolutionSeries: [],
        errors: undefined,
      }));
    case 'SET_BACK_TEST_EXPANDED_SECTION':
      return replaceKeyIfExist(state, action.tabId, s => ({
        ...s,
        expandedData: { ...s.expandedData, [action.section]: action.isExpanded },
      }));
    default:
      return state;
  }
}
const validateMaturities = (dateMode: DateMode, maturities: readonly string[]): BackTestFormError | undefined => {
  if (dateMode === 'multi') {
    return maturities.length > 0 ? undefined : { fields: ['maturities'] };
  }
  const invalidRange = interval(subYears(maturities[1], 5), maturities[1]);
  return isWithinInterval(maturities[0], invalidRange)
    ? { fields: ['maturities'], translationKey: 'backTest.errors.invalidMaturitiesRange', severity: 'Error' }
    : undefined;
};
const validateSecondQuoteId = (quoteId?: string | null): BackTestFormError | undefined =>
  quoteId === undefined || isNonEmpty(quoteId) ? undefined : { fields: ['quoteIds'], isMandatory: true };

const mapPointsToSerie = <T extends string | number>(quoteId: string, serie: BackTestResponseTimeSerie<T>) => ({
  quoteId,
  data: serie?.map(point => [point.index, point.value] as Point<T>) ?? [],
});

const getFormErrors = (maturities: readonly string[], dateMode: DateMode, secondQuoteId?: string | null) =>
  [validateSecondQuoteId(secondQuoteId), validateMaturities(dateMode, maturities)].filter(isDefined);
